/**
 * RouteAnnouncer — tells assistive technology that the page changed and moves the
 * keyboard focus to the new page's `<h1>` (#3621).
 *
 * - Fires on a change of `pathname` only: a query or fragment change
 *   (`?page=2`) neither announces nor moves the focus, and neither does the
 *   first load (the browser already read the page).
 * - Announces the screen title WITHOUT the brand suffix, in a polite live region.
 * - Focus goes to the first `<h1>` of `#main-content`, or to `main` itself when the
 *   page has not rendered its heading yet (lazy routes); in that case it follows
 *   to the heading as soon as it appears, but only while the focus is still on `main`.
 */

import { useEffect, useRef, useState } from 'react'

import { useLocation } from 'react-router-dom'

import { useRouteMeta } from './useRouteMeta'

const MAIN_ID = 'main-content'
/** How long a lazy page may take to render its heading before we stop waiting. */
const HEADING_WAIT_MS = 5000

function focusElement(element: HTMLElement): void {
  if (!element.hasAttribute('tabindex')) element.setAttribute('tabindex', '-1')
  element.classList.add('focus:outline-none')
  element.focus()
}

/** Returns a cleanup when it has to wait for a heading that is not rendered yet. */
function focusPageHeading(): (() => void) | undefined {
  const main = document.getElementById(MAIN_ID)
  if (!main) return undefined

  const heading = main.querySelector<HTMLElement>('h1')
  if (heading) {
    focusElement(heading)
    return undefined
  }

  focusElement(main)
  const observer = new MutationObserver(() => {
    const late = main.querySelector<HTMLElement>('h1')
    if (!late) return
    observer.disconnect()
    if (document.activeElement === main) focusElement(late)
  })
  observer.observe(main, { childList: true, subtree: true })
  const timer = setTimeout(() => observer.disconnect(), HEADING_WAIT_MS)
  return () => {
    clearTimeout(timer)
    observer.disconnect()
  }
}

export const RouteAnnouncer: React.FC = () => {
  const { pathname } = useLocation()
  const { state, title } = useRouteMeta()
  // `n` is the React key of the announced text: a fresh node is inserted for EACH
  // announcement, so a screen reader speaks it even when the title is the same as
  // the previous one (/support/1 -> /support/2 share a title).
  const [announcement, setAnnouncement] = useState({ n: 0, text: '' })
  const lastPathname = useRef(pathname)
  const announcePending = useRef(false)

  useEffect(() => {
    if (lastPathname.current === pathname) return
    lastPathname.current = pathname
    announcePending.current = true
    setAnnouncement((previous) => ({ n: previous.n, text: '' }))
  }, [pathname])

  useEffect(() => {
    if (!announcePending.current) return undefined
    if (state === 'pending') {
      announcePending.current = false
      return undefined
    }
    if (title === null) return undefined // catalogue still loading: wait for it
    announcePending.current = false
    setAnnouncement((previous) => ({ n: previous.n + 1, text: title }))
    return focusPageHeading()
  }, [pathname, state, title])

  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      <span key={announcement.n}>{announcement.text}</span>
    </div>
  )
}
